<template>
  <div class="permissoes-da-pessoa" data-testid="users-permissoes">
    <!-- 1. Função -->
    <section class="permissoes-secao" data-testid="users-permissoes-funcao">
      <h4 class="permissoes-pergunta">{{ t('Qual é a função desta pessoa?') }}</h4>
      <p class="permissoes-ajuda">
        {{ t('A função define o que ela vê e faz nas franquias a que está vinculada. Escolha uma só.') }}
      </p>

      <p v-if="funcoesDaPessoa.length > 1" class="permissoes-aviso" data-testid="users-permissoes-funcao-acumulada">
        <i class="pi pi-exclamation-triangle"></i>
        {{ t('Esta pessoa acumula mais de uma função') }}: {{ funcoesDaPessoa.map((f) => t(f.rotulo)).join(', ') }}.
        {{ t('Vale a mais alta. Escolher uma função abaixo deixa só a escolhida.') }}
      </p>

      <div class="permissoes-cartoes">
        <button
          v-for="funcao in funcoesVisiveis"
          :key="funcao.grupo"
          type="button"
          class="permissoes-cartao"
          :class="{
            'is-marcado': tem(funcao.grupo),
            'is-principal': funcaoPrincipal?.grupo === funcao.grupo,
            'is-travado': !podeMudar(funcao.grupo),
          }"
          :disabled="disabled || !podeMudar(funcao.grupo)"
          :aria-pressed="tem(funcao.grupo)"
          :data-testid="`users-permissoes-funcao-${funcao.grupo}`"
          @click="escolherFuncao(funcao)"
        >
          <span class="permissoes-cartao-topo">
            <span class="permissoes-marca"><i v-if="tem(funcao.grupo)" class="pi pi-check"></i></span>
            <strong class="permissoes-rotulo">{{ t(funcao.rotulo) }}</strong>
            <span v-if="funcao.sensivel" class="permissoes-etiqueta is-sensivel">{{ t('Acesso total') }}</span>
            <span v-if="!podeMudar(funcao.grupo)" class="permissoes-etiqueta">{{ t('Você não pode alterar') }}</span>
          </span>
          <span class="permissoes-resumo">{{ t(funcao.resumo) }}</span>
          <span v-if="funcao.pode?.length || funcao.nao_pode?.length" class="permissoes-listas">
            <span v-for="item in funcao.pode ?? []" :key="`p-${item}`" class="permissoes-item is-pode">
              <i class="pi pi-check-circle"></i> {{ t(item) }}
            </span>
            <span v-for="item in funcao.nao_pode ?? []" :key="`n-${item}`" class="permissoes-item is-nao-pode">
              <i class="pi pi-times-circle"></i> {{ t(item) }}
            </span>
          </span>
          <span class="permissoes-tecnico">{{ t('grupo') }}: {{ funcao.grupo }}</span>
        </button>
      </div>

      <button
        v-if="funcoesDaPessoa.some((f) => podeMudar(f.grupo))"
        type="button"
        class="permissoes-link"
        :disabled="disabled"
        data-testid="users-permissoes-funcao-nenhuma"
        @click="semFuncao"
      >
        {{ t('Deixar sem função na franquia') }}
      </button>
    </section>

    <!-- 2. Acessos a mais, agrupados pelo sistema a que pertencem -->
    <section v-if="areasVisiveis.length" class="permissoes-secao" data-testid="users-permissoes-areas">
      <h4 class="permissoes-pergunta">{{ t('Ela usa mais algum sistema ou cuida de mais alguma coisa?') }}</h4>
      <p class="permissoes-ajuda">
        {{ t('Acessos a mais, independentes da função, separados por sistema. Ligue só o que a pessoa realmente usa.') }}
      </p>
      <div
        v-for="area in areasVisiveis"
        :key="area.nome"
        class="permissoes-area"
        :class="{ 'is-ativa': area.ligados > 0 }"
        :data-testid="`users-permissoes-area-${area.nome}`"
      >
        <div class="permissoes-area-topo">
          <i :class="area.deFora ? 'pi pi-external-link' : 'pi pi-th-large'"></i>
          <strong class="permissoes-area-nome">{{ t(area.nome) }}</strong>
          <span v-if="area.deFora" class="permissoes-etiqueta">{{ t('Aplicativo de fora') }}</span>
          <span class="permissoes-area-conta">
            {{ area.ligados }} {{ t('de') }} {{ area.grupos.length }} {{ t('ligado(s)') }}
          </span>
        </div>
        <p v-if="area.deFora" class="permissoes-ajuda">
          {{ t('Vale em outro aplicativo, que usa o mesmo login. Não abre nenhuma tela aqui.') }}
        </p>
        <div v-for="secao in area.secoes" :key="secao.nome" class="permissoes-subsecao">
          <div v-if="secao.nome" class="permissoes-subsecao-nome">{{ t(secao.nome) }}</div>
          <div
            v-for="item in secao.grupos"
            :key="item.grupo"
            class="permissoes-linha"
            :class="{ 'is-marcado': tem(item.grupo), 'is-travado': !podeMudar(item.grupo) }"
            :data-testid="`users-permissoes-grupo-${item.grupo}`"
          >
            <input-toggle
              :key="`${item.grupo}-${versao}`"
              :elem-id="`permissao-${item.grupo}`"
              :model-value="tem(item.grupo)"
              :label="''"
              :disabled="disabled || !podeMudar(item.grupo)"
              :data-testid="`users-permissoes-toggle-${item.grupo}`"
              @update:model-value="(valor: boolean) => alternar(item, valor)"
            />
            <span class="permissoes-linha-texto">
              <strong class="permissoes-rotulo">
                {{ t(item.rotulo) }}
                <span v-if="item.sensivel" class="permissoes-etiqueta is-sensivel">{{ t('Acesso sensível') }}</span>
                <span v-if="!podeMudar(item.grupo)" class="permissoes-etiqueta">{{ t('Você não pode alterar') }}</span>
              </strong>
              <span class="permissoes-resumo">{{ t(item.resumo) }}</span>
              <span v-if="item.rotulo !== item.grupo" class="permissoes-tecnico">{{ t('grupo') }}: {{ item.grupo }}</span>
            </span>
          </div>
        </div>
      </div>
    </section>

    <!-- Grupos que o catálogo não explica: aparecem como estão, nunca somem. -->
    <section v-if="outrosVisiveis.length" class="permissoes-secao" data-testid="users-permissoes-outros">
      <h4 class="permissoes-pergunta">{{ t('Outros grupos') }}</h4>
      <p class="permissoes-ajuda">{{ t('Grupos ainda sem descrição. Aparecem com o nome técnico.') }}</p>
      <div
        v-for="grupo in outrosVisiveis"
        :key="grupo"
        class="permissoes-linha"
        :class="{ 'is-marcado': tem(grupo), 'is-travado': !podeMudar(grupo) }"
        :data-testid="`users-permissoes-outro-${grupo}`"
      >
        <input-toggle
          :key="`${grupo}-${versao}`"
          :elem-id="`permissao-${grupo}`"
          :model-value="tem(grupo)"
          :label="''"
          :disabled="disabled || !podeMudar(grupo)"
          :data-testid="`users-permissoes-toggle-${grupo}`"
          @update:model-value="(valor: boolean) => definir(grupo, valor)"
        />
        <span class="permissoes-linha-texto"><strong class="permissoes-rotulo">{{ grupo }}</strong></span>
      </div>
    </section>

    <!-- Resumo -->
    <section class="permissoes-secao permissoes-resumo-final" data-testid="users-permissoes-resumo">
      <h4 class="permissoes-pergunta">{{ t('Em resumo, esta pessoa') }}</h4>
      <ul>
        <li v-if="funcaoPrincipal" data-testid="users-permissoes-resumo-funcao">
          {{ t('é') }} <strong>{{ t(funcaoPrincipal.rotulo) }}</strong>: {{ t(funcaoPrincipal.resumo) }}
        </li>
        <li v-else data-testid="users-permissoes-resumo-funcao">
          {{ t('não tem função em franquia: não vê dados de nenhuma unidade.') }}
        </li>
        <li v-for="area in areasDaPessoa" :key="area.nome">
          <strong>{{ t(area.nome) }}</strong>: {{ area.grupos.map((g) => t(g.rotulo)).join(', ') }}
        </li>
        <li v-if="sensiveisDaPessoa.length" class="permissoes-resumo-alerta">
          <i class="pi pi-exclamation-triangle"></i>
          {{ t('Atenção, acesso sensível') }}: {{ sensiveisDaPessoa.map((g) => t(g.rotulo)).join(', ') }}
        </li>
      </ul>
    </section>
  </div>
</template>

<script setup lang="ts">
/**
 * PermissoesDaPessoa — os grupos de uma conta, lidos como responsabilidades.
 *
 * A aba mostrava uma lista única de botões com o nome técnico de cada grupo do Cognito. Aqui
 * eles viram três perguntas (função, responsabilidades a mais, aplicativos de fora), com o nome
 * que a pessoa lê, uma frase de responsabilidade e o que pode e não pode.
 *
 * É SÓ apresentação: o valor continua sendo a lista de grupos, e nome, descrição e a pergunta em
 * que cada grupo entra vêm do app-api (useRegrasDeGrupos → catalogo). Nenhum nome de grupo
 * decide nada nesta tela. O que a pessoa que edita não pode atribuir aparece travado — e o
 * servidor recusa do mesmo jeito.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRegrasDeGrupos } from '@/composables/useRegrasDeGrupos';
import { useConfirmAction } from '@/composables/useConfirmAction';
import type { Usuarios_GrupoApresentadoRow } from '@/contracts/Usuarios_GrupoApresentadoRow';

const { t } = useI18n();
const { confirmAction } = useConfirmAction();
const { regras, carregar } = useRegrasDeGrupos();
void carregar();

const props = withDefaults(defineProps<{
  /** Os grupos que a conta tem. */
  modelValue?: string[];
  /** Os grupos que quem edita pode atribuir e tirar. */
  availableGroups?: string[];
  disabled?: boolean;
}>(), {
  modelValue: () => [],
  availableGroups: () => [],
  disabled: false,
});

const emit = defineEmits<{ 'update:modelValue': [value: string[]] }>();

type Grupo = Usuarios_GrupoApresentadoRow;

const catalogo = computed<Grupo[]>(() =>
  [...(regras.value.catalogo ?? [])].sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0))
);

const tem = (grupo: string): boolean => props.modelValue.includes(grupo);
const podeMudar = (grupo: string): boolean => props.availableGroups.includes(grupo);
/** Entra na tela o que quem edita pode atribuir e o que a conta já tem (este, travado). */
const visivel = (grupo: string): boolean => podeMudar(grupo) || tem(grupo);

const doTipo = (tipo: string): Grupo[] => catalogo.value.filter((g) => g.tipo === tipo && visivel(g.grupo));

const funcoesVisiveis = computed<Grupo[]>(() => doTipo('funcao'));

/** Tudo o que não é função, agrupado pelo sistema a que pertence (a área vem do app-api). */
interface Secao { nome: string; grupos: Grupo[] }
interface Area { nome: string; deFora: boolean; grupos: Grupo[]; secoes: Secao[]; ligados: number }
const porArea = (grupos: Grupo[]): Area[] => {
  const areas: Area[] = [];
  for (const g of grupos) {
    const nome = g.area || g.app || '';
    let area = areas.find((a) => a.nome === nome);
    if (!area) { area = { nome, deFora: g.tipo === 'app', grupos: [], secoes: [], ligados: 0 }; areas.push(area); }
    area.grupos.push(g);
    // Dentro da área, as subdivisões que o dono declarou ("Papéis", "Comitês"), na ordem em que vêm.
    const nomeDaSecao = g.secao ?? '';
    let secao = area.secoes.find((x) => x.nome === nomeDaSecao);
    if (!secao) { secao = { nome: nomeDaSecao, grupos: [] }; area.secoes.push(secao); }
    secao.grupos.push(g);
    if (tem(g.grupo)) area.ligados += 1;
  }
  return areas;
};
const areasVisiveis = computed<Area[]>(() =>
  porArea(catalogo.value.filter((g) => g.tipo !== 'funcao' && visivel(g.grupo)))
);

const noCatalogo = computed<Set<string>>(() => new Set(catalogo.value.map((g) => g.grupo)));
const outrosVisiveis = computed<string[]>(() =>
  [...new Set([...props.availableGroups, ...props.modelValue])].filter((g) => !noCatalogo.value.has(g))
);

// O que a conta tem hoje. A ordem do catálogo já é do topo da escada para a base, então a
// primeira função que a conta tem é a que vale.
const funcoesDaPessoa = computed<Grupo[]>(() => catalogo.value.filter((g) => g.tipo === 'funcao' && tem(g.grupo)));
const funcaoPrincipal = computed<Grupo | null>(() => funcoesDaPessoa.value[0] ?? null);
const areasDaPessoa = computed<Area[]>(() =>
  porArea(catalogo.value.filter((g) => g.tipo !== 'funcao' && tem(g.grupo)))
);
const sensiveisDaPessoa = computed<Grupo[]>(() => catalogo.value.filter((g) => g.sensivel && tem(g.grupo)));

const gravar = (grupos: string[]): void => emit('update:modelValue', [...new Set(grupos)]);

/** Acesso sensível pede confirmação ao ser CONCEDIDO; tirar nunca pede. */
const comConfirmacao = (grupo: Grupo, concede: () => void, desiste: () => void = () => {}): void => {
  if (!grupo.sensivel) { concede(); return; }
  confirmAction({
    header: t('Conceder acesso sensível'),
    message: `${t(grupo.rotulo)}: ${t(grupo.resumo)} ${t('Confirma?')}`,
    acceptLabel: t('Conceder'),
    rejectLabel: t('Cancelar'),
    onAccept: concede,
    onReject: desiste,
  });
};

/** Função é escolha única: entra a escolhida e saem as outras que quem edita pode tirar. */
const escolherFuncao = (funcao: Grupo): void => {
  if (tem(funcao.grupo) && funcoesDaPessoa.value.length === 1) return;
  const saem = funcoesDaPessoa.value.map((f) => f.grupo).filter((g) => g !== funcao.grupo && podeMudar(g));
  comConfirmacao(funcao, () =>
    gravar([...props.modelValue.filter((g) => !saem.includes(g)), funcao.grupo])
  );
};

const semFuncao = (): void => {
  const saem = funcoesDaPessoa.value.map((f) => f.grupo).filter(podeMudar);
  gravar(props.modelValue.filter((g) => !saem.includes(g)));
};

/** Liga ou desliga um grupo pelo nome. */
const definir = (grupo: string, ligado: boolean): void => {
  if (ligado === tem(grupo)) return;
  gravar(ligado ? [...props.modelValue, grupo] : props.modelValue.filter((g) => g !== grupo));
};

// O interruptor já virou na tela quando a confirmação aparece; se a pessoa desiste, o dado não
// mudou e nada re-renderiza — remontar os interruptores os traz de volta ao que o dado diz.
const versao = ref(0);

const alternar = (grupo: Grupo, ligado: boolean): void => {
  if (!ligado) { definir(grupo.grupo, false); return; }
  comConfirmacao(grupo, () => definir(grupo.grupo, true), () => { versao.value += 1; });
};
</script>

<style scoped>
.permissoes-da-pessoa { display: flex; flex-direction: column; gap: 28px; padding: 8px 0 16px; }
.permissoes-secao { display: flex; flex-direction: column; gap: 10px; }
.permissoes-pergunta { margin: 0; font-size: 1.1rem; font-weight: 600; color: #1f2937; }
.permissoes-ajuda { margin: 0; color: #6b7280; font-size: 0.9rem; }
.permissoes-aviso {
  margin: 0; padding: 10px 12px; border-radius: 8px; background: #fffbeb; border: 1px solid #fcd34d;
  color: #92400e; font-size: 0.9rem;
}
.permissoes-cartoes { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 12px; }
.permissoes-cartao {
  display: flex; flex-direction: column; gap: 8px; text-align: left; padding: 14px;
  border: 2px solid #e5e7eb; border-radius: 8px; background: #fff; cursor: pointer;
  font: inherit; color: inherit;
}
.permissoes-cartao:hover:not(:disabled) { border-color: #93c5fd; }
.permissoes-cartao.is-marcado { border-color: #93c5fd; background: #f8fbff; }
.permissoes-cartao.is-principal { border-color: #2563eb; background: #eff6ff; }
.permissoes-cartao:disabled { cursor: not-allowed; opacity: 0.75; }
.permissoes-cartao-topo { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.permissoes-marca {
  width: 20px; height: 20px; border-radius: 50%; border: 2px solid #9ca3af; display: inline-flex;
  align-items: center; justify-content: center; font-size: 0.65rem; color: #fff; flex: none;
}
.is-marcado .permissoes-marca { background: #2563eb; border-color: #2563eb; }
.permissoes-rotulo { font-size: 1rem; color: #111827; }
.permissoes-resumo { color: #374151; font-size: 0.9rem; }
.permissoes-listas { display: flex; flex-direction: column; gap: 4px; }
.permissoes-item { font-size: 0.85rem; display: flex; gap: 6px; align-items: baseline; }
.permissoes-item.is-pode { color: #166534; }
.permissoes-item.is-nao-pode { color: #991b1b; }
.permissoes-tecnico { color: #9ca3af; font-size: 0.75rem; }
.permissoes-etiqueta {
  font-size: 0.7rem; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: #f3f4f6;
  color: #4b5563; white-space: nowrap;
}
.permissoes-etiqueta.is-sensivel { background: #fee2e2; color: #991b1b; }
.permissoes-link {
  align-self: flex-start; background: none; border: none; padding: 0; color: #2563eb; cursor: pointer;
  font: inherit; font-size: 0.85rem; text-decoration: underline;
}
.permissoes-linha {
  display: flex; gap: 12px; align-items: flex-start; padding: 12px 14px; border: 1px solid #e5e7eb;
  border-radius: 8px; background: #fff; margin: 0; font-weight: normal;
}
.permissoes-linha.is-marcado { border-color: #93c5fd; background: #f8fbff; }
.permissoes-linha.is-travado { opacity: 0.75; }
.permissoes-linha-texto { display: flex; flex-direction: column; gap: 4px; }
.permissoes-linha-texto .permissoes-rotulo { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.permissoes-linha :deep(.toggle-label) { display: none; }
.permissoes-linha :deep(.toggle-container) { flex: none; margin: 0; padding: 0; }
.permissoes-area {
  display: flex; flex-direction: column; gap: 8px; padding: 14px; border: 1px solid #e5e7eb;
  border-radius: 12px; background: #fafafa;
}
.permissoes-area.is-ativa { border-color: #93c5fd; background: #f5f9ff; }
.permissoes-area-topo { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; color: #374151; }
.permissoes-area-nome { font-size: 1rem; color: #111827; }
.permissoes-subsecao { display: flex; flex-direction: column; gap: 8px; }
.permissoes-subsecao-nome {
  margin-top: 4px; font-size: 0.78rem; font-weight: 700; letter-spacing: 0.04em; text-transform: uppercase;
  color: #6b7280;
}
.permissoes-area-conta { margin-left: auto; font-size: 0.8rem; color: #6b7280; }
.permissoes-resumo-final { padding: 14px 16px; border-radius: 8px; background: #f9fafb; border: 1px solid #e5e7eb; }
.permissoes-resumo-final ul { margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px; }
.permissoes-resumo-alerta { color: #991b1b; list-style: none; margin-left: -18px; }
</style>
